import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Flame,
  Droplets,
  Zap,
  Wrench,
  Stethoscope,
  Fish,
  Mountain,
  Dumbbell,
  CheckCircle2,
  ArrowRight,
  ArrowLeft,
  Loader2,
  Phone,
  Building2,
  Home,
  Trees,
  Car,
  Utensils,
  Sparkles,
  Search,
  Plus
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const iconMap = {
  flame: Flame,
  droplets: Droplets,
  zap: Zap,
  wrench: Wrench,
  stethoscope: Stethoscope,
  fish: Fish,
  mountain: Mountain,
  dumbbell: Dumbbell,
  home: Home,
  trees: Trees,
  car: Car,
  utensils: Utensils,
  sparkles: Sparkles
};

export default function CustomerOnboarding() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    business_name: '',
    business_type: '',
    custom_industry: '',
    owner_name: '',
    email: '',
    phone: '',
    location: '',
    timezone: '',
    business_hours: '',
    services: [],
    custom_notes: '',
    calendar_system: 'google'
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/templates`);
      setTemplates(response.data.templates);
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const selectBusinessType = (type) => {
    setFormData({ ...formData, business_type: type, custom_industry: '' });
  };

  const selectCustomIndustry = () => {
    setFormData({ ...formData, business_type: 'custom' });
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/customers/onboard`, formData);
      toast.success('Customer onboarded successfully!');
      navigate(`/admin/clients/${response.data.customer_id}`);
    } catch (error) {
      console.error('Error onboarding customer:', error);
      toast.error('Failed to onboard customer');
    } finally {
      setSubmitting(false);
    }
  };

  const canProceed = () => {
    if (step === 1) {
      if (formData.business_type === 'custom') {
        return formData.custom_industry.trim() !== '';
      }
      return formData.business_type !== '';
    }
    if (step === 2) return formData.business_name && formData.owner_name && formData.email && formData.phone;
    if (step === 3) return true;
    return true;
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-900/50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-sky-400 to-blue-600 flex items-center justify-center">
              <Phone className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-lg">Alaska AI Services</span>
            <span className="text-slate-500 ml-2">/ Customer Onboarding</span>
          </div>
        </div>
      </header>

      {/* Progress Bar */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-8">
        <div className="flex items-center justify-between mb-8">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                step >= s ? 'bg-sky-500 text-white' : 'bg-slate-800 text-slate-500'
              }`}>
                {step > s ? <CheckCircle2 className="w-5 h-5" /> : s}
              </div>
              {s < 3 && (
                <div className={`w-24 sm:w-32 h-1 mx-2 ${
                  step > s ? 'bg-sky-500' : 'bg-slate-800'
                }`} />
              )}
            </div>
          ))}
        </div>
        <div className="flex justify-between text-sm text-slate-400 mb-8">
          <span>Business Type</span>
          <span>Business Info</span>
          <span>Review</span>
        </div>
      </div>

      {/* Form Steps */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 pb-16">
        {/* Step 1: Select Business Type */}
        {step === 1 && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <h2 className="text-2xl font-bold mb-2">What type of business?</h2>
            <p className="text-slate-400 mb-8">Select the industry to get a pre-built AI template</p>
            
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-sky-400" />
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {templates.map((template) => {
                  const IconComponent = iconMap[template.icon] || Building2;
                  return (
                    <button
                      key={template.id}
                      onClick={() => selectBusinessType(template.id)}
                      className={`p-6 rounded-xl border text-left transition-all ${
                        formData.business_type === template.id
                          ? 'bg-sky-500/20 border-sky-500 text-white'
                          : 'bg-slate-900/50 border-slate-800 hover:border-slate-700 text-slate-300'
                      }`}
                      data-testid={`template-${template.id}`}
                    >
                      <IconComponent className={`w-8 h-8 mb-3 ${
                        formData.business_type === template.id ? 'text-sky-400' : 'text-slate-500'
                      }`} />
                      <div className="font-semibold">{template.name}</div>
                    </button>
                  );
                })}
              </div>
            )}
            
            {/* Custom Industry Option */}
            <div className="mt-8 pt-8 border-t border-slate-800">
              <p className="text-slate-400 mb-4 flex items-center gap-2">
                <Search className="w-4 h-4" />
                Don't see your industry? Enter it here:
              </p>
              <div className="flex gap-3 max-w-md">
                <Input
                  value={formData.custom_industry}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    custom_industry: e.target.value,
                    business_type: e.target.value ? 'custom' : ''
                  })}
                  placeholder="e.g., Pet Grooming, Photography Studio..."
                  className={`bg-slate-800/50 border-slate-700 text-white flex-1 ${
                    formData.business_type === 'custom' ? 'border-sky-500' : ''
                  }`}
                  data-testid="custom-industry-input"
                />
                {formData.custom_industry && (
                  <div className="flex items-center text-sky-400">
                    <Plus className="w-5 h-5 mr-1" />
                    <span className="text-sm">Custom</span>
                  </div>
                )}
              </div>
              <p className="text-xs text-slate-500 mt-2">
                We'll create a customized AI template specifically for your industry.
              </p>
            </div>
          </motion.div>
        )}

        {/* Step 2: Business Info */}
        {step === 2 && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <h2 className="text-2xl font-bold mb-2">Business Information</h2>
            <p className="text-slate-400 mb-8">Tell us about the business</p>
            
            <div className="space-y-6 max-w-xl">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Business Name *</label>
                <Input
                  name="business_name"
                  value={formData.business_name}
                  onChange={handleInputChange}
                  placeholder="My Business Here"
                  className="bg-slate-800/50 border-slate-700 text-white"
                  data-testid="input-business-name"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Owner Name *</label>
                <Input
                  name="owner_name"
                  value={formData.owner_name}
                  onChange={handleInputChange}
                  placeholder="Your Name"
                  className="bg-slate-800/50 border-slate-700 text-white"
                  data-testid="input-owner-name"
                />
              </div>
              
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Email *</label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="you@business.com"
                    className="bg-slate-800/50 border-slate-700 text-white"
                    data-testid="input-email"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Phone *</label>
                  <Input
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="000-000-0000"
                    className="bg-slate-800/50 border-slate-700 text-white"
                    data-testid="input-phone"
                  />
                </div>
              </div>
              
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Location</label>
                  <Input
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    placeholder="City, State"
                    className="bg-slate-800/50 border-slate-700 text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Time Zone</label>
                  <select
                    name="timezone"
                    value={formData.timezone}
                    onChange={handleInputChange}
                    className="w-full h-10 px-3 rounded-md bg-slate-800/50 border border-slate-700 text-white focus:outline-none focus:ring-2 focus:ring-sky-500"
                    data-testid="select-timezone"
                  >
                    <option value="">Select your timezone</option>
                    <option value="America/New_York">Eastern (ET)</option>
                    <option value="America/Chicago">Central (CT)</option>
                    <option value="America/Denver">Mountain (MT)</option>
                    <option value="America/Los_Angeles">Pacific (PT)</option>
                    <option value="America/Anchorage">Alaska (AKT)</option>
                    <option value="Pacific/Honolulu">Hawaii (HT)</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Business Hours</label>
                <Input
                  name="business_hours"
                  value={formData.business_hours}
                  onChange={handleInputChange}
                  placeholder="Mon-Fri 8am-5pm"
                  className="bg-slate-800/50 border-slate-700 text-white"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Calendar System</label>
                <select
                  name="calendar_system"
                  value={formData.calendar_system}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 bg-slate-800/50 border border-slate-700 rounded-md text-white"
                >
                  <option value="google">Google Calendar</option>
                  <option value="outlook">Outlook/Microsoft</option>
                  <option value="apple">Apple Calendar</option>
                  <option value="other">Other</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Custom Notes</label>
                <Textarea
                  name="custom_notes"
                  value={formData.custom_notes}
                  onChange={handleInputChange}
                  placeholder="Any special instructions, pricing info, or FAQs..."
                  className="bg-slate-800/50 border-slate-700 text-white"
                  rows={4}
                />
              </div>
            </div>
          </motion.div>
        )}

        {/* Step 3: Review */}
        {step === 3 && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <h2 className="text-2xl font-bold mb-2">Review & Submit</h2>
            <p className="text-slate-400 mb-8">Confirm the details before creating the customer</p>
            
            <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6 max-w-xl">
              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-slate-400">Business Type</span>
                  <span className="font-medium">
                    {formData.business_type === 'custom' 
                      ? <span className="text-sky-400">{formData.custom_industry} (Custom)</span>
                      : templates.find(t => t.id === formData.business_type)?.name
                    }
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Business Name</span>
                  <span className="font-medium">{formData.business_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Owner</span>
                  <span className="font-medium">{formData.owner_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Email</span>
                  <span className="font-medium">{formData.email}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Phone</span>
                  <span className="font-medium">{formData.phone}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Location</span>
                  <span className="font-medium">{formData.location || 'Not specified'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Time Zone</span>
                  <span className="font-medium">{formData.timezone || 'Not specified'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Hours</span>
                  <span className="font-medium">{formData.business_hours || 'Not specified'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Calendar</span>
                  <span className="font-medium capitalize">{formData.calendar_system}</span>
                </div>
                {formData.custom_notes && (
                  <div className="pt-4 border-t border-slate-800">
                    <span className="text-slate-400 block mb-2">Notes</span>
                    <span className="text-sm">{formData.custom_notes}</span>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-8 max-w-xl">
          {step > 1 ? (
            <Button
              onClick={() => setStep(step - 1)}
              variant="outline"
              className="border-slate-700 text-slate-300"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          ) : (
            <div />
          )}
          
          {step < 3 ? (
            <Button
              onClick={() => setStep(step + 1)}
              disabled={!canProceed()}
              className="bg-sky-500 hover:bg-sky-600 text-white"
            >
              Continue
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-green-500 hover:bg-green-600 text-white"
              data-testid="submit-onboarding"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <CheckCircle2 className="w-4 h-4 mr-2" />
              )}
              Create Customer
            </Button>
          )}
        </div>
      </main>
    </div>
  );
}
